import {
  GrowthBook,
  Experiment,
  Result,
  FeatureResult,
} from "@growthbook/growthbook-react";
import { ExperimentClient, ExperimentConfig } from "../../types/experiment";
import Cookies, { CookieAttributes } from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import { getRootDomain, isHttps } from "../../utils/browserUtils";
import { AppFeatures } from "src/types/growthbook/app-features";
import { datadogRum } from "@datadog/browser-rum";

export const GB_UUID_COOKIE = "gb_anonymous_id";

export const getGrowthBookCookie = () => {
  let uuid = Cookies.get(GB_UUID_COOKIE);
  const host = window.location.host;
  const domain = getRootDomain(host);

  const cookieAttributes: CookieAttributes = isHttps()
    ? {
        domain: domain,
        secure: true,
        sameSite: "None",
      }
    : {
        domain: domain,
      };

  if (uuid) {
    // To ensure that existing cookies have the correct domain, we remove the
    // cookie and set it again. There is a chance that the cookie was set without
    // a domain, so we attempt to remove it with and without a domain.
    Cookies.remove(GB_UUID_COOKIE, { domain });
    Cookies.remove(GB_UUID_COOKIE);
    Cookies.set(GB_UUID_COOKIE, uuid, cookieAttributes);
  } else {
    uuid = uuidv4();
    Cookies.set(GB_UUID_COOKIE, uuid, cookieAttributes);
  }

  return Cookies.get(GB_UUID_COOKIE);
};

export class GrowthBookClient implements ExperimentClient {
  private _client: GrowthBook<AppFeatures>;
  private _clientKey: string;

  constructor(config?: ExperimentConfig) {
    this._clientKey = process.env.GROWTHBOOK_CLIENT_KEY;

    this._client = new GrowthBook<AppFeatures>({
      apiHost: config?.apiHost || "https://cdn.growthbook.io",
      clientKey: this._clientKey,
      enableDevMode: config?.enableDevMode ?? true,
      trackingCallback: (experiment: Experiment<any>, result: Result<any>) => {
        config?.onExperimentViewed?.(experiment.key, result.key);
      },
      onFeatureUsage: (key: string, result: FeatureResult<any>) => {
        datadogRum.addFeatureFlagEvaluation(key, result?.value);
      },
    });
  }

  async init() {
    await this._client.init({ streaming: true });
  }

  async setAttributes(attributes: Record<string, any>) {
    return this._client.setAttributes(attributes);
  }

  get instance() {
    return this._client;
  }
}

export class GrowthBookMockClient extends GrowthBookClient {
  constructor() {
    super();
  }

  async init() {
    return Promise.resolve();
  }

  async setAttributes() {
    return Promise.resolve();
  }

  async setTrackingCallback() {
    return Promise.resolve();
  }
}

export const growthBookClient = new GrowthBookClient();
export const mockGrowthBookClient = new GrowthBookMockClient();
